import { userApi } from '@/api/request/user';
import router from '@/router';
import { message } from 'ant-design-vue';
import md5 from 'md5';
import { useAppStore } from '@/store/appStore';
import { Toast } from '@/enum/ui';
import { LoginSessionKey } from '@/enum/app';

export interface ILoginFormProp {
  username: string;
  password: string;
}

/**格式化密码 */
export const formatPassword = ({
  username,
  password,
}: ILoginFormProp): string => md5(md5(username) + md5(password));

/**
 * 建立 platform（IAM）会话：登录响应由 OTA 后端附带 platform_session
 * （受信子系统换发的用户令牌 + IAM 权限点），一次写入 dd_token 与权限点。
 *
 * 替代旧"unionId → /dingding/sign_union_id 重签 + /dingding/info_by_union_code
 * 查权限"两跳：sign_union_id 任意 unionId 即可换取他人 platform token
 * （unionId 不是秘密，属冒充口子），已移除。令牌为短时效 access（2h），
 * 每次 OTA 登录/会话恢复时由后端重换，无需前端续期。
 */
export function setPlatformSession(payload?: {
  token?: string;
  permission?: string[];
}) {
  if (!payload?.token) return;
  const appStore = useAppStore();
  appStore.setDDToken(payload.token);
  appStore.setIAMPermission(payload.permission ?? []);
}

/**登录函数 */
export const loginMethod = async (
  loginParam: ILoginFormProp,
  isSilence = false,
) => {
  try {
    const { data } = await userApi.login(loginParam);
    const appStore = useAppStore();

    if (data.status === 200) {
      // 登录信息 -- 同步
      appStore.setLoginState(data.data);
      // platform 会话随登录下发（账密用户未绑钉钉或平台不可用时缺省跳过）
      setPlatformSession(data.data.platform_session);

      sessionStorage.setItem(
        LoginSessionKey.LOGIN_PARAM,
        JSON.stringify(loginParam),
      );
      if (!isSilence) {
        // 主动登录视为换用户，清空上一个用户留下的页签
        appStore.resetRouteTags();
        message.success(data.message);
        await router.push('/');
      }
    } else {
      !isSilence && message.error(data.message);
    }
  } catch (error) {
    message.error({ content: '网络错误', key: Toast.NetError });
    sessionStorage.removeItem(LoginSessionKey.LOGIN_PARAM) /**清空登录信息 */;
    await router.push('/login');
  }
};

export async function loginByUnionId(union_id: string, isSilence = false) {
  try {
    const appStore = useAppStore();

    const { data } = await userApi.dingtalkLogin(union_id);
    if (data.status === 200) {
      appStore.setLoginState(data.data);
      sessionStorage.setItem(LoginSessionKey.UNION_ID, union_id);

      // platform 会话随登录下发（含静默恢复：每次恢复后端都会重换新令牌）
      setPlatformSession(data.data.platform_session);

      if (!isSilence) {
        // 主动登录视为换用户，清空上一个用户留下的页签
        appStore.resetRouteTags();
        message.success(data.message);
        await router.push('/');
      }
    } else {
      message.error(data.message);
    }
  } catch (e) {
    message.error({ content: '网络错误', key: Toast.NetError });
    sessionStorage.removeItem(LoginSessionKey.LOGIN_PARAM) /**清空登录信息 */;
    await router.push('/login');
  }
}

/**
 * 刷新token
 * */
export async function refreshToken() {
  try {
    const { data } = await userApi.refreshToken();
    const appStore = useAppStore();

    if (data.status === 200) {
      appStore.setLoginState(data.data);
    } else {
      await router.push('/login');
    }
  } catch (error) {
    message.error('网络错误');
    sessionStorage.removeItem(LoginSessionKey.LOGIN_PARAM) /**清空登录信息 */;
    await router.push('/login');
  }
}
